import { Heading } from '@lmig/lmds-react-typography';
import { KeyContactsPanelType, SLIDERPANELTYPES } from '../Models/Panels';
import '../../../Styles/KeyContacts.scss';
import { useEffect, useContext, useState } from 'react';
import { useUserData } from 'Foundation/User/UserContext';
import { GetKeyContacts } from 'Feature/Slider/SliderApi';
import { CreateNavSections } from '../../../Utilities/KeyContactsUtility';
import { CircularProgressSpinner } from '@lmig/sales-ui-circular-progress-spinner';
import { Notification } from '@lmig/lmds-react-notification';
import { AccordionLinks } from './AccordionLinks';
import { SliderContext } from '../SliderContext';
import { ISliderContext } from '../Models/Panels';
import { SearchInput } from '@lmig/lmds-react-alphanumeric-input';
import SitecoreLink from 'Foundation/GlassMapper/SitecoreLink';
import ContactDetailSubPanel from './ContactDetailSubPanel';
import { StringHelper } from 'Utilities/StringHelper';
import { removeTextFromStringCaseInsensitive } from 'Utilities/StringFormatter';

interface IKeyContactsPanel {
  panel: KeyContactsPanelType;
  isOpen: boolean;
}

export interface IKeyContact {
  PortalRoleName: string;
  EnterpriseID: string;
  FirstName: string;
  LastName: string;
  DirectDial: string;
  TollFree: string;
  Mobile: string;
  Fax: string;
  Email: string;
  MgrPortalRoleName: string;
  MgrEnterpriseID: string;
  Department?: string; //specific to static contacts
  Title?: string; //specific to static contacts
  isStaticContact: boolean; //specific to static contacts
  Link?: ISitecoreLink; //specific to static contacts
}

export function KeyContactsPanel({ panel, isOpen }: IKeyContactsPanel) {
  const { userData } = useUserData();
  const [localPanel, setLocalPanel] = useState<KeyContactsPanelType>(panel); //panel is passed as a prop to slider after api call completes
  const [activeKeyContact, setActiveKeyContact] = useState<IKeyContact>();
  const sliderContext = useContext<ISliderContext>(SliderContext);
  const [searchValue, setSearchValue] = useState('');
  const [sliderPanelLoaded, setSliderPanelLoaded] = useState<boolean>(false);
  const [contactsError, setContactsError] = useState<boolean>();
  const textToRemoveFromSmallCommercialRoles = 'SAFECO';

  useEffect(() => {
    getKeyContacts();
  }, []);

  const removeTextFromContacts = (contacts: IKeyContact[], textToRemove: string): IKeyContact[] => {
    return contacts?.map((contact) => {
      return { ...contact, 
        PortalRoleName: removeTextFromStringCaseInsensitive(contact.PortalRoleName, textToRemove)
      };
    });
  };

  const setContextContactsSmallCommercial = (contacts: IKeyContact[]) => {
    try {
      if (contacts && contacts != undefined) {
        sliderContext.setContacts(removeTextFromContacts(contacts, textToRemoveFromSmallCommercialRoles));
      }
    }
    catch (err) {
      console.error(err);
    }
  };

  const getKeyContacts = async () => {
    try {
      setContactsError(false);
      if (userData && userData.selectedStatCodeData && !sliderContext.contacts && !sliderContext.contactsLoading) {
        sliderContext.setContactsLoading(true);
        let data = await GetKeyContacts(userData.selectedStatCodeData.SubStat, userData.state);
        if (data && data.success) {
          if (data.KeyContacts && userData?.isBL()) {
            setContextContactsSmallCommercial(data.KeyContacts);
          } else {
            sliderContext.setContacts(data.KeyContacts);
          }
        } else setContactsError(true);
        sliderContext.setContactsLoading(false);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!sliderContext.contacts && !contactsError) sliderContext.setContactsLoading(true);
    else sliderContext.setContactsLoading(false);
  }, [sliderContext.contacts, contactsError]);

  useEffect(() => {
    if (localPanel && !sliderContext.panels) {
      sliderContext.addPanel(localPanel);
      setSliderPanelLoaded(true);
    } else if (!localPanel && sliderContext.panels && sliderContext.panels.length) {
      let _panel = sliderContext.panels.find((x) => {
        if (x.type != SLIDERPANELTYPES.LINKSPANEL && x.link && x.link.includes('https'))
          return onFullPage(new URL(x.link).pathname);
        else if (x.type != SLIDERPANELTYPES.LINKSPANEL && x.link) return onFullPage(x.link);
      });
      if (_panel && _panel.type === SLIDERPANELTYPES.KEYCONTACTSPANEL) {
        setLocalPanel(_panel);
        setSliderPanelLoaded(true);
      } //full page view sets panel from context after slider panel updates it
    }
  }, [sliderContext, sliderContext.panels, localPanel]);

  const callback = (targetId: string | undefined) => {
    if (targetId && !StringHelper.isGuid(targetId)) {
      const record = sliderContext.contacts?.find(function (rec) {
        return (
          (rec.FirstName + ' ' + rec.LastName + ' ' + rec.PortalRoleName)?.toLowerCase() === targetId?.toLowerCase()
        );
      });
      setActiveKeyContact(record);
    } else if (targetId && StringHelper.isGuid(targetId)) {
      let staticContact = findStaticContact(targetId);
      if (staticContact !== undefined) {
        let record: IKeyContact = {
          PortalRoleName: '',
          EnterpriseID: '',
          FirstName: '',
          LastName: '',
          DirectDial: '',
          TollFree: '',
          MgrEnterpriseID: '',
          MgrPortalRoleName: '',
          Department: staticContact.Department,
          Email: staticContact.Email ?? '',
          Fax: staticContact.Fax ?? '',
          Mobile: staticContact.Phone ?? '',
          Title: staticContact.Title ?? '',
          isStaticContact: true,
          Link: staticContact.Link,
        };
        setActiveKeyContact(record);
      }
    }
    window.scrollTo(0, 0);
  };

  const findStaticContact = (targetId: string): IContactLink | undefined => {
    let contact: IContactLink | undefined;
    localPanel.ContactSection.forEach((_panel) => {
      if (_panel.ContactLinks?.find((item) => item.Id === targetId)) {
        contact = _panel.ContactLinks?.find((item) => item.Id === targetId);
      }
    });
    return contact;
  };

  const setContactBreadCrumbCallback = (contact: IKeyContact | undefined) => {
    setActiveKeyContact(contact);
  };

  useEffect(() => {
    if (sliderContext.contacts && sliderContext.panels) {
      // Static Contact Links
      if (localPanel?.ContactSection) {
        let _lclPanel = localPanel;
        _lclPanel.ContactSection.forEach((section) => {
          if (section.ContactLinks) {
            section.ContactLinks.forEach((cl) => {
              cl.OnClick = callback;
              if (cl.Link) {
                cl.Link.CaretPosition = 'none';
              }
            });
          }
        });
        setLocalPanel(_lclPanel);
      }

      // Sales Accordion filtering
      let salesContacts = sliderContext.contacts?.filter(
        (x) =>
          x.PortalRoleName?.toLowerCase().includes('territory manager') ||
          x.PortalRoleName?.toLowerCase().includes('regional director'),
      );
      let salesLinks: IContactLink[] | undefined = salesContacts?.map((x) => ({
        IncludedStates: userData?.state ?? '',
        Id: x.FirstName + ' ' + x.LastName + ' ' + x.PortalRoleName,
        Link: {
          TargetId: x.FirstName + ' ' + x.LastName + ' ' + x.PortalRoleName,
          Class: '',
          Target: '_blank',
          Text: x.FirstName + ' ' + x.LastName,
          SubText: x.PortalRoleName,
          Url: ' ', //to bypass empty url conditional check
          IsKeyContactLink: true,
          Favoritable: false,
        },
        OnClick: (targetId: string | undefined) => callback(targetId),
      }));

      if (salesLinks && localPanel?.ContactSection)
        setLocalPanel((localPanel) => ({
          ...localPanel,
          ContactSection: localPanel.ContactSection.map((x: IContactSection) =>
            x.Title === 'Sales' ? { ...x, ContactLinks: [...(x.ContactLinks ?? []), ...(salesLinks ?? [])] } : x,
          ),
        }));

      // Underwriting Accordion filtering
      let uwContacts = sliderContext.contacts?.filter(
        (x) =>
          x.PortalRoleName.toLowerCase().includes('underwriter') ||
          x.PortalRoleName.toLowerCase().includes('underwriting'),
      );
      let uwLinks: IContactLink[] | undefined = uwContacts?.map((x) => ({
        IncludedStates: userData?.state ?? '',
        Id: x.FirstName + ' ' + x.LastName + ' ' + x.PortalRoleName,
        Link: {
          TargetId: x.FirstName + ' ' + x.LastName + ' ' + x.PortalRoleName,
          Class: '',
          Target: '_blank',
          Text: x.FirstName + ' ' + x.LastName,
          SubText: x.PortalRoleName,
          Url: ' ', //to bypass empty url conditional check
          IsKeyContactLink: true,
          Favoritable: false,
        },
        OnClick: (targetId: string | undefined) => callback(targetId),
      }));

      if (uwLinks && localPanel?.ContactSection)
        setLocalPanel((localPanel) => ({
          ...localPanel,
          ContactSection: localPanel.ContactSection.map((x: IContactSection) =>
            x.Title === 'Underwriting' ? { ...x, ContactLinks: [...(x.ContactLinks ?? []), ...(uwLinks ?? [])] } : x,
          ),
        }));
    }
  }, [sliderContext?.contacts, sliderContext.panels, sliderPanelLoaded]);

  useEffect(() => {
    sliderContext.addContactsPanel(localPanel);
  }, [localPanel]);

  const onFullPage = (link: string) => link?.toLowerCase() === window.location.pathname?.toLowerCase();

  useEffect(() => {
    if (!isOpen) setActiveKeyContact(undefined);
  }, [isOpen]);

  return (
    <>
      {!activeKeyContact && localPanel && <Heading type="h4">{localPanel.title}</Heading>}
      {!activeKeyContact && localPanel && (
        <div data-theme="evolved">
          <SearchInput
            id="Alphanumeric--default"
            labelVisual={`Search ${localPanel.title?.toLowerCase()}`}
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
            }}
            aria-live="polite"
            onClearClicked={() => setSearchValue('')}
          />
        </div>
      )}
      {!activeKeyContact && localPanel?.link && (
        <div style={{ paddingBottom: '1em ' }}>
          <SitecoreLink
            Class="full-page-view-link"
            Target="_self"
            Text={'Full page view'}
            Url={localPanel.link}
            CaretPosition="right"
          />
        </div>
      )}
      {!activeKeyContact ? (
        sliderContext.contactsLoading ? (
          <CircularProgressSpinner
            className="key-contacts-spinner"
            size="32"
            aria-label="Loading portal users data..."
          />
        ) : contactsError ? (
          <Notification alert="Our system is unable to complete the request." highlightType="negative">
            Please try again later.
          </Notification>
        ) : sliderContext.contacts && sliderContext.contacts.length === 0 ? (
          <p>There are no contacts for this state.</p>
        ) : (
          localPanel?.ContactSection && (
            <>
              <AccordionLinks
                sections={CreateNavSections(localPanel.ContactSection)}
                usesFavorites={false}
                id={localPanel.title}
                filterTerm={searchValue}
                usesContactLinks={true}
              />
            </>
          )
        )
      ) : (
        <ContactDetailSubPanel
          activeKeyContact={activeKeyContact}
          OnClick={setContactBreadCrumbCallback}
        ></ContactDetailSubPanel>
      )}
    </>
  );
}

export default KeyContactsPanel;
